import { type CSSProperties, useCallback, useMemo, useRef } from 'react';

import classNames from 'classnames';

import User from '@/icon/User';

import { getBackgroundColorStyle } from '../../../utils/background';
import { computeColor } from '../../../utils/colors';
import { computeFont } from '../../../utils/fonts';
import { getFontSizeVariables } from '../../../utils/getFontSizeVariables';
import { getHeightStyle, getWidthStyle } from '../../../utils/sizing';

import * as styles from '../../Navbar.css';
import {
  NAVBAR_MENU_ITEM_BORDER_DEFAULTS,
  NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2,
  NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2,
} from '../NavbarMenuItem.constants';
import type { NavbarMenuItemV2ElementProps } from '../NavbarMenuItem.type';
import { NavbarMenuItemDropdownV2 } from './NavbarMenuItemDropdown';

export function NavbarMenuItemUserButtonV2(
  props: NavbarMenuItemV2ElementProps & {
    controlbyselected: boolean | string;
  },
) {
  const { element, className, onClick, attributes, children } = props;
  const elementRef = useRef<HTMLDivElement>(null);
  const isInsideBurgerContent = !!elementRef?.current
    ?.closest('.navbar-menu-burger-content')
    ?.contains(elementRef.current);

  const cleanHref = useMemo(() => {
    const href = element?.attrs?.href;
    const isCustomLink = element?.attrs?.isCustomLink;

    if (!href) return href;

    try {
      const url = new URL(href);
      return isCustomLink ? href : href.replace(url.origin, '');
    } catch {
      return href;
    }
  }, [element?.attrs?.href, element?.attrs?.isCustomLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onClick) return;
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  if (element.attrs?.type !== 'user_button') {
    return null;
  }

  const visible = element.attrs?.visible ?? true;
  if (!visible) {
    return null;
  }

  const {
    label,
    new_tab,
    firstName,
    lastName,
    email,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    fontFamily,
    style,
    fontSize,
    fontWeight,
    width,
    height,
    letterSpacing,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
    mobileColor,
    mobileBorderColor,
    mobileBorderWidth,
    mobileBorderStyle,
    mobileBorderRadius,
    mobileBackground,
    mobilePadding,
  } = element.attrs || {};

  const action = element.attrs?.action;
  const renderableStyle = style || 'dropdown';

  // Don't use hoverBorderRadius if hoverBorderStyle is none or undefined
  const evaluatedHoverBorderRadius =
    (!['none', undefined].includes(hoverBorderStyle) && hoverBorderRadius) ||
    borderRadius ||
    NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius;

  const image = <User size={16} weight="bold" />;

  if (action === 'profile') {
    if (renderableStyle === 'button') {
      return (
        <a
          {...props}
          {...customAttributes}
          onClick={handleClick}
          className={classNames(styles.navbarItemButtonV2, className, customAttributes?.className)}
          style={
            {
              '--padding': padding ?? '0px',
              '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
              '--border-color': borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
              '--border-style': borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
              '--border-width': borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
              '--background-color': getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background)
                .background,
              '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
              '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontFamily,
              '--letter-spacing': letterSpacing ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.letterSpacing,
              '--width': width ? getWidthStyle(width).width : '32px',
              '--height': height ? getHeightStyle(height).height : '32px',
              '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
              '--max-width': width ? getWidthStyle(width).maxWidth : '32px',
              width: 'fit-content',

              '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color,
              '--hover-background-color':
                hoverBackgroundColor || background || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background,
              '--hover-border-radius': evaluatedHoverBorderRadius,
              '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
              '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
              '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
              '--mobile-background-color': getBackgroundColorStyle(
                mobileBackground || background || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.background,
              ).background,
              '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
              '--mobile-border-color': mobileBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
              '--mobile-border-width': mobileBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
              '--mobile-border-style': mobileBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
              '--mobile-border-radius':
                mobileBorderRadius || borderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
            } as CSSProperties
          }
          href={cleanHref}
          target={new_tab ? '_blank' : '_self'}
          title={!firstName ? email : `${firstName}${lastName ? ` ${lastName}` : ''}`}
        >
          {image}
        </a>
      );
    }

    return (
      <NavbarMenuItemDropdownV2
        {...props}
        evaluatedHoverBorderRadius={evaluatedHoverBorderRadius}
        showCaret={false}
        forceShowAccordion={isInsideBurgerContent}
        labelComponent={
          <div
            ref={elementRef}
            style={
              {
                '--width': width ? getWidthStyle(width).width : '32px',
                '--height': height ? getHeightStyle(height).height : '32px',
                '--max-width': width ? getWidthStyle(width).maxWidth : '32px',
                width: 'fit-content',
                '--padding': padding ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.padding,
                '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
                '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color),
                '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontFamily,
                ...getFontSizeVariables(fontSize ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontSize),
                '--font-weight': fontWeight ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontWeight,
                '--letter-spacing': letterSpacing ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.letterSpacing,
                '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
                '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
                '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
                '--background-color': getBackgroundColorStyle(
                  background ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background,
                ).background,
                '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color),
                '--hover-border-radius': evaluatedHoverBorderRadius,
                '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color,
                '--hover-background-color':
                  hoverBackgroundColor || background || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background,
                '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
                '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
                '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
                '--mobile-background-color': getBackgroundColorStyle(
                  mobileBackground || background || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background,
                ).background,
                '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.padding,
                '--mobile-border-color':
                  mobileBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
                '--mobile-border-width':
                  mobileBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
                '--mobile-border-style':
                  mobileBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
                '--mobile-border-radius':
                  mobileBorderRadius || borderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
              } as CSSProperties
            }
          >
            {image}
          </div>
        }
      >
        {children}
      </NavbarMenuItemDropdownV2>
    );
  }

  return (
    <a
      {...attributes}
      {...props}
      {...customAttributes}
      className={classNames(styles.navbarItemButtonV2, className, customAttributes?.className)}
      style={
        {
          '--padding': padding ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.padding,
          '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
          ...getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background),
          '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color),
          '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontFamily,
          ...getFontSizeVariables(fontSize ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontSize),
          '--font-weight': fontWeight ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.fontWeight,
          '--letter-spacing': letterSpacing ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.letterSpacing,
          '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
          '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
          '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
          '--background-color': getBackgroundColorStyle(background ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background)
            .background,
          '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color),
          '--hover-border-radius': evaluatedHoverBorderRadius,
          '--hover-text-color': hoverTextColor || color || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.color,
          '--hover-background-color':
            hoverBackgroundColor || background || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background,
          '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
          '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
          '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
          '--mobile-background-color': getBackgroundColorStyle(
            mobileBackground || background || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.background,
          ).background,
          '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_BUTTON_DEFAULTS_V2.padding,
          '--mobile-border-color': mobileBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
          '--mobile-border-width': mobileBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
          '--mobile-border-style': mobileBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
          '--mobile-border-radius': mobileBorderRadius || borderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
        } as CSSProperties
      }
      href={cleanHref}
      target={new_tab ? '_blank' : '_self'}
    >
      {renderableStyle === 'button' ? children : label}
    </a>
  );
}
